import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import { AutoAwesome } from '@mui/icons-material';
import api from '../../services/api';

interface HoroscopeProps {
  birthMonthDay?: string;
  isFlat?: boolean;
}


interface HoroscopeData {
  sign: string;
  date_range: string;
  current_date: string;
  description: string;
}

const zodiacSigns = [
  { name: 'Aries', start: '03-21', end: '04-19', symbol: '♈' },
  { name: 'Taurus', start: '04-20', end: '05-20', symbol: '♉' },
  { name: 'Gemini', start: '05-21', end: '06-20', symbol: '♊' },
  { name: 'Cancer', start: '06-21', end: '07-22', symbol: '♋' },
  { name: 'Leo', start: '07-23', end: '08-22', symbol: '♌' },
  { name: 'Virgo', start: '08-23', end: '09-22', symbol: '♍' },
  { name: 'Libra', start: '09-23', end: '10-22', symbol: '♎' },
  { name: 'Scorpio', start: '10-23', end: '11-21', symbol: '♏' },
  { name: 'Sagittarius', start: '11-22', end: '12-21', symbol: '♐' },
  { name: 'Capricorn', start: '12-22', end: '01-19', symbol: '♑' },
  { name: 'Aquarius', start: '01-20', end: '02-18', symbol: '♒' },
  { name: 'Pisces', start: '02-19', end: '03-20', symbol: '♓' }
];

const getZodiacSign = (birthMonthDay: string): { name: string; symbol: string; dateRange: string } | null => {
  if (!birthMonthDay || !birthMonthDay.match(/^\d{2}-\d{2}$/)) {
    return null;
  }

  const [month, day] = birthMonthDay.split('-').map(Number);

  for (const sign of zodiacSigns) {
    const { name, start, end, symbol } = sign;
    const [startMonth, startDay] = start.split('-').map(Number);
    const [endMonth, endDay] = end.split('-').map(Number);

    // Handle year wrap (Capricorn)
    if (startMonth > endMonth) {
      if (month === startMonth && day >= startDay) {
        return { name, symbol, dateRange: `${start} to ${end}` };
      }
      if (month === endMonth && day <= endDay) {
        return { name, symbol, dateRange: `${start} to ${end}` };
      }
      if (month > startMonth || month < endMonth) {
        return { name, symbol, dateRange: `${start} to ${end}` };
      }
    } else {
      if (
        (month === startMonth && day >= startDay) ||
        (month === endMonth && day <= endDay) ||
        (month > startMonth && month < endMonth)
      ) {
        return { name, symbol, dateRange: `${start} to ${end}` };
      }
    }
  }

  return null;
};

const DailyHoroscope: React.FC<HoroscopeProps> = ({ birthMonthDay, isFlat }) => {
  const [horoscope, setHoroscope] = useState<HoroscopeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchHoroscope = async () => {
      const zodiacInfo = birthMonthDay ? getZodiacSign(birthMonthDay) : null;

      if (!zodiacInfo) {
        setError('Birthday not set or invalid format');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Call backend API for horoscope data
        const sign = zodiacInfo.name.toLowerCase();
        const response = await api.get(`/horoscope/${sign}`);

        if (response.data.success && response.data.data) {
          setHoroscope(response.data.data);
        } else {
          throw new Error('Failed to fetch horoscope - no data in response');
        }
      } catch (err: any) {
        console.error('Error fetching horoscope:', err);
        const errorMessage = err.response?.data?.error || err.response?.data?.message || err.message || 'Unable to load horoscope. Please try again later.';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchHoroscope();
  }, [birthMonthDay]);

  const renderContent = () => {
    if (!birthMonthDay) {
      return (
        <Box sx={{ p: isFlat ? 2 : 0 }}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <AutoAwesome color="primary" />
            <Typography variant="h6">Daily Horoscope</Typography>
          </Box>
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Set your birthday in your profile to see your daily horoscope!
          </Alert>
        </Box>
      );
    }

    if (loading) {
      return (
        <Box sx={{ p: isFlat ? 2 : 0 }}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <AutoAwesome color="primary" />
            <Typography variant="h6">Daily Horoscope</Typography>
          </Box>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={150}>
            <CircularProgress size={32} thickness={4} />
          </Box>
        </Box>
      );
    }

    if (error) {
      const zodiacInfo = birthMonthDay ? getZodiacSign(birthMonthDay) : null;
      return (
        <Box sx={{ p: isFlat ? 2 : 0 }}>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <AutoAwesome color="primary" />
            <Typography variant="h6">Daily Horoscope</Typography>
          </Box>
          {zodiacInfo && (
            <Box
              sx={{
                textAlign: 'center',
                mb: 2,
                p: 2,
                bgcolor: 'action.hover',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Typography variant="h3" sx={{ mb: 0.5 }}>{zodiacInfo.symbol}</Typography>
              <Typography variant="h6" gutterBottom>{zodiacInfo.name}</Typography>
              <Typography variant="caption" color="text.secondary">{zodiacInfo.dateRange}</Typography>
            </Box>
          )}
          <Alert severity="info" sx={{ borderRadius: 2 }}>{error}</Alert>
        </Box>
      );
    }

    const zodiacInfo = birthMonthDay ? getZodiacSign(birthMonthDay) : null;
    if (!zodiacInfo) return null;

    return (
      <Box sx={{ p: isFlat ? 2 : 0 }}>
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <AutoAwesome color="primary" />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>Daily Horoscope</Typography>
        </Box>
        <Box
          sx={{
            textAlign: 'center',
            mb: 2,
            p: 2,
            background: 'linear-gradient(135deg, #ff8c00 0%, #ed6c02 100%)',
            color: 'white',
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(237, 108, 2, 0.2)'
          }}
        >
          <Typography variant="h3" sx={{ mb: 0.5, filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }}>
            {zodiacInfo.symbol}
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            {zodiacInfo.name}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9, fontWeight: 500 }}>
            {zodiacInfo.dateRange}
          </Typography>
        </Box>
        {horoscope && (
          <Box sx={{ px: 0.5 }}>
            <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.7, color: 'text.primary', fontSize: '0.9rem' }}>
              {horoscope.description}
            </Typography>
            {horoscope.current_date && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', mt: 2, textAlign: 'right', fontStyle: 'italic', opacity: 0.8 }}
              >
                {horoscope.current_date}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  };

  if (isFlat) {
    return renderContent();
  }

  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <CardContent sx={{ p: 3 }}>
        {renderContent()}
      </CardContent>
    </Card>
  );
};


export default DailyHoroscope;

